import type { FC, FormEvent } from 'react'
import type { BurstPiece } from '@/components/contact/burst'

import { useEffect, useId, useState } from 'react'
import { ArrowRight, CalendarClock, Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { Burst, makeBurst } from '@/components/contact/burst'
import { CALL_TIMES, CONTACT_TIMEZONE } from '@/constants/contact'

type Day = {
    iso: string
    weekday: string
    date: string
    month: string
    label: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const buildDays = (count = 10): Array<Day> => {
    const days: Array<Day> = []
    const cursor = new Date()

    cursor.setHours(12, 0, 0, 0)
    cursor.setDate(cursor.getDate() + 1)

    while (days.length < count) {
        const weekday = cursor.getDay()

        if (weekday !== 0 && weekday !== 6) {
            days.push({
                iso: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
                weekday: cursor.toLocaleDateString('en-GB', { weekday: 'short' }),
                date: String(cursor.getDate()),
                month: cursor.toLocaleDateString('en-GB', { month: 'short' }),
                label: cursor.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
            })
        }

        cursor.setDate(cursor.getDate() + 1)
    }

    return days
}

const isTaken = (iso: string, time: string): boolean => {
    let hash = 7

    for (const character of `${iso}${time}`) hash = (hash * 31 + character.charCodeAt(0)) >>> 0

    return hash % 4 === 0
}

const input = 'h-14 w-full rounded-2xl border border-cream/15 bg-cream/[0.06] px-5 text-base text-cream outline-none transition-[border-color,box-shadow] placeholder:text-cream/40 focus:border-ember focus:ring-4 focus:ring-ember/20'

export const BookCall: FC = () => {
    const id = useId()
    const [days, setDays] = useState<Array<Day> | null>(null)
    const [dayIndex, setDayIndex] = useState(0)
    const [time, setTime] = useState<string | null>(null)
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [error, setError] = useState('')
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
    const [pieces, setPieces] = useState<Array<BurstPiece>>([])

    useEffect(() => {
        setDays(buildDays())
    }, [])

    const day = days?.[dayIndex] ?? null

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!day || !time) {
            setError('Pick a day and a slot first.')

            return
        }

        if (name.trim().length < 2) {
            setError('We will need a name to say hello.')

            return
        }

        if (!EMAIL_PATTERN.test(email.trim())) {
            setError('That email address looks a bit off.')

            return
        }

        setError('')
        setStatus('sending')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kind: 'call',
                    name: name.trim(),
                    email: email.trim(),
                    slot: `${day.label}, ${time} ${CONTACT_TIMEZONE}`
                })
            })

            const data = await response.json().catch(() => ({})) as { message?: string }

            if (!response.ok) {
                setError(data.message ?? 'Something went wrong. Please try again.')
                setStatus('idle')

                return
            }

            setStatus('sent')

            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) setPieces(makeBurst(24))
        }
        catch {
            setError('We could not reach the server. Check your connection and try again.')
            setStatus('idle')
        }
    }

    return (
        <section
            id='book-a-call'
            aria-labelledby='book-a-call-title'
            className='mx-auto max-w-[1400px] scroll-mt-24 px-5 pb-6 pt-10 md:px-10'
        >
            <Reveal>
                <div className='grain grain-light relative overflow-hidden rounded-[2rem] bg-ink p-6 text-cream [--grain-opacity:0.25] md:rounded-[2.5rem] md:p-12'>
                    <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                        <div>
                            <SectionLabel tone='cream'>Twenty minutes, free</SectionLabel>
                            <h2
                                id='book-a-call-title'
                                className='mt-5 text-[clamp(2.4rem,5vw,4.5rem)] font-bold leading-[0.92] tracking-[-0.04em]'
                            >
                                Book a free 20 minute call.
                                <span className='block text-ember'>Pick a slot.</span>
                            </h2>
                        </div>
                        <p className='max-w-sm text-base leading-relaxed text-cream/70'>
                            Twenty minutes with a strategist, a quick read of your feed and one idea you can use straight away. All times are in {CONTACT_TIMEZONE}.
                        </p>
                    </div>

                    {status === 'sent' && day && time ? (
                        <div
                            role='status'
                            className='relative mt-12 flex flex-col items-center rounded-[1.75rem] bg-cream/[0.06] px-6 py-14 text-center'
                        >
                            <Burst pieces={pieces} />
                            <span className='flex size-16 items-center justify-center rounded-full bg-ember text-ink'>
                                <Check
                                    className='size-7'
                                    strokeWidth={2.5}
                                />
                            </span>
                            <p className='mt-6 font-heading text-3xl font-bold tracking-[-0.035em] md:text-4xl'>
                                You are booked in.
                            </p>
                            <p className='mt-3 max-w-md text-cream/75'>
                                We have pencilled in {day.label} at {time}. A confirmation will land at {email.trim()} within a few hours.
                            </p>
                        </div>
                    ) : (
                        <div className='mt-12 grid gap-10 lg:grid-cols-[1.45fr_1fr] lg:gap-12'>
                            <div>
                                <p
                                    id={`${id}-days`}
                                    className='font-mono text-[11px] uppercase tracking-[0.16em] text-cream/60'
                                >
                                    Choose a day
                                </p>

                                <div
                                    role='radiogroup'
                                    aria-labelledby={`${id}-days`}
                                    className='mt-4 grid grid-cols-5 gap-2'
                                >
                                    {days ? days.map((item, index) => {
                                        const selected = index === dayIndex

                                        return (
                                            <button
                                                key={item.iso}
                                                type='button'
                                                role='radio'
                                                aria-checked={selected}
                                                aria-label={item.label}
                                                onClick={() => {
                                                    setDayIndex(index)
                                                    setTime(null)
                                                }}
                                                className={cn(
                                                    'flex flex-col items-center rounded-2xl border px-1 py-3 transition-[background-color,border-color,color] duration-200',
                                                    selected ? 'border-ember bg-ember text-ink' : 'border-cream/12 hover:border-cream/40'
                                                )}
                                            >
                                                <span className={cn('font-mono text-[10px] uppercase', selected ? 'text-ink/70' : 'text-cream/55')}>{item.weekday}</span>
                                                <span className='mt-1 font-heading text-2xl font-bold leading-none tracking-[-0.04em]'>{item.date}</span>
                                                <span className={cn('mt-1 font-mono text-[10px]', selected ? 'text-ink/70' : 'text-cream/55')}>{item.month}</span>
                                            </button>
                                        )
                                    }) : Array.from({ length: 10 }).map((_, index) => (
                                        <span
                                            key={index}
                                            className='h-[84px] animate-pulse rounded-2xl bg-cream/[0.06]'
                                        />
                                    ))}
                                </div>

                                <p
                                    id={`${id}-times`}
                                    className='mt-8 font-mono text-[11px] uppercase tracking-[0.16em] text-cream/60'
                                >
                                    Choose a time
                                </p>

                                <div
                                    role='radiogroup'
                                    aria-labelledby={`${id}-times`}
                                    className='mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5'
                                >
                                    {CALL_TIMES.map(slot => {
                                        const taken = day ? isTaken(day.iso, slot) : false
                                        const selected = time === slot

                                        return (
                                            <button
                                                key={slot}
                                                type='button'
                                                role='radio'
                                                aria-checked={selected}
                                                disabled={!day || taken}
                                                onClick={() => setTime(slot)}
                                                className={cn(
                                                    'h-12 rounded-full border font-mono text-sm transition-[background-color,border-color,color] duration-200',
                                                    selected && 'border-cream bg-cream text-ink',
                                                    !selected && !taken && 'border-cream/15 hover:border-cream/50',
                                                    taken && 'cursor-not-allowed border-cream/5 text-cream/25 line-through'
                                                )}
                                            >
                                                {slot}
                                                {taken && <span className='sr-only'> (taken)</span>}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            <form
                                onSubmit={onSubmit}
                                noValidate
                                className='flex flex-col gap-3 rounded-[1.75rem] bg-cream/[0.05] p-5 md:p-6'
                            >
                                <div className='flex items-start gap-3 rounded-2xl bg-cream/[0.06] p-4'>
                                    <CalendarClock className='mt-0.5 size-5 shrink-0 text-ember' />
                                    <p
                                        aria-live='polite'
                                        className='text-[15px] leading-snug'
                                    >
                                        {day && time ? (
                                            <>
                                                <span className='block font-semibold'>{day.label}</span>
                                                <span className='text-cream/70'>{time}, 20 minutes, video call</span>
                                            </>
                                        ) : (
                                            <span className='text-cream/70'>Pick a day and a time to request your call.</span>
                                        )}
                                    </p>
                                </div>

                                <label
                                    htmlFor={`${id}-call-name`}
                                    className='sr-only'
                                >
                                    Your name
                                </label>
                                <input
                                    id={`${id}-call-name`}
                                    autoComplete='name'
                                    placeholder='Your name'
                                    value={name}
                                    onChange={event => setName(event.target.value)}
                                    className={input}
                                />

                                <label
                                    htmlFor={`${id}-call-email`}
                                    className='sr-only'
                                >
                                    Email address
                                </label>
                                <input
                                    id={`${id}-call-email`}
                                    type='email'
                                    autoComplete='email'
                                    inputMode='email'
                                    placeholder='Email address'
                                    value={email}
                                    onChange={event => setEmail(event.target.value)}
                                    className={input}
                                />

                                {error && (
                                    <p
                                        role='alert'
                                        className='text-sm text-[#ff9a66]'
                                    >
                                        {error}
                                    </p>
                                )}

                                <button
                                    type='submit'
                                    disabled={status === 'sending'}
                                    className='group mt-2 inline-flex h-14 items-center justify-between gap-4 rounded-full bg-ember pl-6 pr-2 text-base font-medium text-ink transition-colors hover:bg-[#ff6a14] disabled:opacity-70'
                                >
                                    {status === 'sending' ? 'Booking' : 'Book this slot'}
                                    <span className='flex size-10 items-center justify-center rounded-full bg-ink text-cream'>
                                        {status === 'sending'
                                            ? <LoaderCircle className='size-4 animate-spin' />
                                            : <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />}
                                    </span>
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </Reveal>
        </section>
    )
}
